import {useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import BenchlingConfig from 'BenchlingConfig';
import type {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AgentApprovalUpdateSource} from 'llm/chat-library/ChatDAO';
import {type VoiceUIController, VoiceUIState} from 'llm/realtime-voice/VoiceUIController';
import {useAppSelector} from 'redux-stores/reduceApp';
import type {BenchlingDispatch} from 'redux-stores/Store';

import {
  createVoiceSessionControlId,
  requestVoiceStart,
  requestVoiceStop,
  selectVoiceSessionControlOwner,
  type VoiceSessionControlOwner,
  voiceSessionControlActions,
} from './VoiceSessionControl';

export function useVoiceSessionControlOwner({
  agentType,
  chatSessionId,
  enabled = true,
  ownerId,
}: {
  agentType?: AgentType;
  chatSessionId: string | null;
  enabled?: boolean;
  ownerId?: string;
}): VoiceSessionControlOwner {
  const dispatch = useDispatch<BenchlingDispatch>();
  const [generatedOwnerId] = useState(() => createVoiceSessionControlId('owner'));
  const [ownerInstanceId] = useState(() => createVoiceSessionControlId('instance'));
  const owner = useMemo(
    () => ({ownerId: ownerId ?? generatedOwnerId, ownerInstanceId, chatSessionId}),
    [chatSessionId, generatedOwnerId, ownerId, ownerInstanceId]
  );

  // A different agent invalidates pending intent, while callback churn and input remounts do not.
  useEffect(() => {
    if (!enabled) {
      return;
    }
    dispatch(voiceSessionControlActions.ownerRegistered(owner));
    return () => {
      // The instance check prevents an old unmount from deleting a newer mount for the same chat.
      dispatch(voiceSessionControlActions.ownerReleased(owner));
    };
  }, [agentType, dispatch, enabled, owner]);

  return owner;
}

interface VoiceSessionControlOptions {
  chatSessionId: string | null;
  owner?: VoiceSessionControlOwner;
  controller?: VoiceUIController;
  writeActionsEnabled?: boolean;
  onGrantFullAccess?: (source: AgentApprovalUpdateSource) => Promise<boolean>;
  onStartVoiceFromNewChat?: () => Promise<void>;
  onCancelVoiceHandoff?: () => void;
  isStartingVoiceFromNewChat: boolean;
  onStartError: (error: unknown) => void;
}

/**
 * Connects React-owned audio callbacks and current permissions to Redux commands.
 * Redux decides whether a start may proceed; React supplies the live callbacks and observes changes.
 * Input rendering, focus, and the user's notice-dismissal preference belong to the caller.
 */
export function useVoiceSessionControl({
  chatSessionId,
  owner,
  controller,
  writeActionsEnabled,
  onGrantFullAccess,
  onStartVoiceFromNewChat,
  onCancelVoiceHandoff,
  isStartingVoiceFromNewChat,
  onStartError,
}: VoiceSessionControlOptions): {
  controller: VoiceUIController | undefined;
} {
  const dispatch = useDispatch<BenchlingDispatch>();
  const localOwner = useVoiceSessionControlOwner({chatSessionId, enabled: owner == null});
  const effectiveOwner = owner ?? localOwner;
  const ownerState = useAppSelector((state) => selectVoiceSessionControlOwner(state, effectiveOwner));
  const isRegistered = ownerState != null;

  // A created chat can mount before its permissions arrive. The command waits without consuming
  // the pending handoff, then claims it once and starts this chat's own audio connection.
  const startOwnedSession = controller?.start;
  useEffect(() => {
    if (isRegistered) {
      dispatch(
        requestVoiceStart({
          ...effectiveOwner,
          writeActionsEnabled,
          reason: 'new-chat-handoff',
          attemptAutomaticGrant: false,
          startVoiceSession: startOwnedSession,
        })
      ).catch(onStartError);
    }
  }, [dispatch, effectiveOwner, isRegistered, onStartError, startOwnedSession, writeActionsEnabled]);

  const controlledSession = useMemo<VoiceUIController | undefined>(
    () =>
      controller == null
        ? undefined
        : {
            ...controller,
            state: isStartingVoiceFromNewChat ? VoiceUIState.CONNECTING : controller.state,
            start: () =>
              dispatch(
                requestVoiceStart({
                  ...effectiveOwner,
                  writeActionsEnabled,
                  attemptAutomaticGrant: BenchlingConfig.LLM_ENABLE_AGENT_APPROVALS,
                  grantFullAccess:
                    onGrantFullAccess == null
                      ? undefined
                      : () => onGrantFullAccess(AgentApprovalUpdateSource.VOICE_START),
                  startVoiceSession: onStartVoiceFromNewChat ?? controller.start,
                })
              ),
            stop: () =>
              dispatch(
                requestVoiceStop(
                  effectiveOwner,
                  isStartingVoiceFromNewChat
                    ? () => {
                        onCancelVoiceHandoff?.();
                        return Promise.resolve();
                      }
                    : controller.stop
                )
              ),
          },
    [
      controller,
      dispatch,
      effectiveOwner,
      isStartingVoiceFromNewChat,
      onCancelVoiceHandoff,
      onGrantFullAccess,
      onStartVoiceFromNewChat,
      writeActionsEnabled,
    ]
  );

  return {
    controller: controlledSession,
  };
}
